import { Directive, EventEmitter, inject, Input, NgZone, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { promiseWithCatch, scrollToElement } from 'src/app/common/app.helpers';
import { MaybeNull } from 'src/app/utils';
import { UrlQueryHandlerService } from '../../services/url-query-handler.service';
import { NotificationType } from './component-type.enum';
import { NotificationAction, NotificationDataPayload, NotificationMessagePayload } from './interfaces';

@Directive()
export abstract class NotificationContext {
  @Input({ required: true }) public data: NotificationDataPayload;

  @Output() public actionExecuted: EventEmitter<NotificationAction> = new EventEmitter<NotificationAction>();

  public abstract notificationType: NotificationType;

  private notificationMessagePayload: MaybeNull<NotificationMessagePayload> = null;

  private ngZone = inject(NgZone);
  private router = inject(Router);
  private urlQueryHandlerService = inject(UrlQueryHandlerService);

  public onActionExecuted(action?: string): void {
    this.actionExecuted.emit(this.convertDataToNotificationAction(action));
  }

  public handleRoute(data: NotificationDataPayload): void {
    if (data.routerUrl) {
      this.ngZone.run(() => {
        promiseWithCatch(this.router.navigateByUrl(`${data.routerUrl}`));
        this.urlQueryHandlerService.processUrlFragment();
        this.notificationMessagePayload = null;
      });
    }
  }

  public navigate(chunk: NotificationMessagePayload): void {
    this.subscribeOnUrlFragmentChanges();
    this.notificationMessagePayload = chunk;
    const fragment: string = chunk.fragment;

    if (chunk && chunk.href) {
      this.ngZone.run(() => {
        promiseWithCatch(this.router.navigateByUrl(`${chunk.href}${fragment ? '#' + fragment : ''}`));
        this.urlQueryHandlerService.processUrlFragment();
        this.notificationMessagePayload = null;
      });
    }
  }

  private subscribeOnUrlFragmentChanges(): void {
    let subscription: Subscription = new Subscription();
    subscription = this.urlQueryHandlerService.urlChangesTrigger.subscribe(() => {
      subscription.unsubscribe();

      if (this.notificationMessagePayload && this.notificationMessagePayload.fragment) {
        scrollToElement(this.notificationMessagePayload.fragment);
      }
    });
  }

  private convertDataToNotificationAction(action?: string): NotificationAction {
    return {
      type: action ? (this.data.actions[action] as string) : 'deleteNotification',
      payload: action ? this.data.entityId : '',
      message_sid: this.data.messageSid,
      projectId: this.data.projectId,
      communityId: this.data.communityId,
      participantId: this.data.participantId,
      initiatorRole: this.data.initiatorRole,
      doctorPatientLinkId: this.data.doctorPatientLinkId,
      initiatorEmail: this.data.initiatorEmail || '',
    };
  }
}
